import {
  CheckCircleOutline,
  Close,
  ErrorOutline,
  HelpOutlineOutlined,
  InfoOutlined,
  TaskAltOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import React from "react";
import { useModalStyles } from "./useModalStyles";

export type OptionPopupObjectProps = {
  show: boolean;
  message: React.ReactNode;
  variant?: "info" | "success" | "danger" | "warning" | "confirm" | "custom" | "error";
  buttons?: ButtonComponentProps[];
  title?: string;
  obj?: any;
};

export type ButtonComponentProps = {
  name: string;
  color: "info" | "success" | "warning" | "primary" | "secondary" | "error" | undefined;
  onClick: (obj?: any) => void;
  icon?: React.ReactNode;
};

export type OptionsPopupProps = {
  open: boolean;
  onClose: () => void;
  onSubmit?: (obj?: any) => void;
  variant?: "info" | "success" | "danger" | "warning" | "confirm" | "custom" | "error";
  message: React.ReactNode;
  buttons?: ButtonComponentProps[];
  title?: string;
  obj?: any;
};

// ── Variant config ────────────────────────────────────────────────────────────
const VARIANT_CONFIG = {
  confirm: {
    label:    "Confirmation",
    accent:   "#14B8A6",
    iconBgD:  "rgba(20,184,166,.12)", iconBdrD: "rgba(20,184,166,.20)",
    iconBgL:  "#ECFDF5",              iconBdrL: "#A7F3D0",
    iconColor:"#14B8A6",
    Icon:     CheckCircleOutline,
    okColor:  "#14B8A6",
  },
  success: {
    label:    "Successful",
    accent:   "#22C55E",
    iconBgD:  "rgba(34,197,94,.10)",  iconBdrD: "rgba(34,197,94,.18)",
    iconBgL:  "#F0FDF4",              iconBdrL: "#A7F3D0",
    iconColor:"#22C55E",
    Icon:     TaskAltOutlined,
    okColor:  "#22C55E",
  },
  info: {
    label:    "Information",
    accent:   "#3B82F6",
    iconBgD:  "rgba(59,130,246,.10)", iconBdrD: "rgba(59,130,246,.18)",
    iconBgL:  "#EFF6FF",              iconBdrL: "#BFDBFE",
    iconColor:"#3B82F6",
    Icon:     InfoOutlined,
    okColor:  "#3B82F6",
  },
  warning: {
    label:    "Warning",
    accent:   "#F59E0B",
    iconBgD:  "rgba(245,158,11,.10)", iconBdrD: "rgba(245,158,11,.18)",
    iconBgL:  "#FFFBEB",              iconBdrL: "#FDE68A",
    iconColor:"#F59E0B",
    Icon:     WarningAmberOutlined,
    okColor:  "#F59E0B",
  },
  danger: {
    label:    "Error",
    accent:   "#EF4444",
    iconBgD:  "rgba(239,68,68,.10)",  iconBdrD: "rgba(239,68,68,.18)",
    iconBgL:  "#FEF2F2",              iconBdrL: "#FECACA",
    iconColor:"#EF4444",
    Icon:     ErrorOutline,
    okColor:  "#EF4444",
  },
  error: {
    label:    "Error",
    accent:   "#EF4444",
    iconBgD:  "rgba(239,68,68,.10)",  iconBdrD: "rgba(239,68,68,.18)",
    iconBgL:  "#FEF2F2",              iconBdrL: "#FECACA",
    iconColor:"#EF4444",
    Icon:     ErrorOutline,
    okColor:  "#EF4444",
  },
  custom: {
    label:    "",
    accent:   "#14B8A6",
    iconBgD:  "rgba(20,184,166,.12)", iconBdrD: "rgba(20,184,166,.20)",
    iconBgL:  "#ECFDF5",              iconBdrL: "#A7F3D0",
    iconColor:"#14B8A6",
    Icon:     HelpOutlineOutlined,
    okColor:  "#14B8A6",
  },
} as const;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const OptionsPopup: React.FC<OptionsPopupProps> = ({
  open,
  onClose,
  onSubmit,
  variant = "confirm",
  message,
  buttons,
  title,
  obj,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isDark, paperSx , primaryBtnSx, cancelBtnSx} = useModalStyles();

  const cfg = VARIANT_CONFIG[variant] ?? VARIANT_CONFIG.confirm;
  const popupTitle = title || cfg.label;
  const { Icon } = cfg;

  const iconBg  = isDark ? cfg.iconBgD  : cfg.iconBgL;
  const iconBdr = isDark ? cfg.iconBdrD : cfg.iconBdrL;

  const heroBg  = isDark ? "transparent"           : "#FAFBFD";
  const heroBdr = isDark ? "rgba(255,255,255,.05)"  : "#F1F5F9";
  const titleCl = isDark ? "#E2E8F0"               : "#1E293B";
  const msgCl   = isDark ? "#64748B"               : "#64748B";
  const footBg  = isDark ? "transparent"           : "#FAFBFD";
  const footBdr = isDark ? "rgba(255,255,255,.05)"  : "#F1F5F9";
  const cancelBg = isDark ? "rgba(255,255,255,.06)" : "#F1F5F9";
  const cancelCl = isDark ? "#94A3B8"              : "#64748B";

  const colorHex: Record<string, string> = {
    success:   "#22C55E",
    error:     "#EF4444",
    warning:   "#F59E0B",
    info:      "#3B82F6",
    primary:   isDark ? "#14B8A6" : "#0F766E",
    secondary: isDark ? "#8B5CF6" : "#7C3AED",
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{ zIndex: 1800 }}
      PaperProps={{ sx: paperSx(isMobile) }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0,0,0,0.72)" : "rgba(15,23,42,0.45)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        px: 2.25, py: 1.75,
        background: heroBg,
        borderBottom: `1px solid ${heroBdr}`,
      }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: "9px",
          background: iconBg, border: `1px solid ${iconBdr}`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Icon sx={{ fontSize: 17, color: cfg.iconColor }} />
        </Box>

        <Typography sx={{ flex: 1, fontSize: "0.875rem", fontWeight: 700, color: titleCl, lineHeight: 1 }}>
          {popupTitle}
        </Typography>

        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            width: 26, height: 26, borderRadius: "6px",
            color: isDark ? "#475569" : "#94A3B8",
            "&:hover": { background: isDark ? "rgba(255,255,255,.06)" : "#F1F5F9" },
          }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.25, py: 2 }}>
        <Typography sx={{ fontSize: "0.82rem", color: msgCl, lineHeight: 1.7 }}>
          {message}
        </Typography>
      </Box>

      <Box sx={{
        display: "flex", justifyContent: "flex-end", gap: 1,
        px: 2.25, pt: 1.25, pb: 2,
        background: footBg,
        borderTop: `1px solid ${footBdr}`,
      }}>
        {buttons ? (
          buttons.map((btn, i) => {
            const isCancel = btn.name.toLowerCase() === "cancel";
            const hex = isCancel ? null : (colorHex[btn.color ?? ""] ?? cfg.okColor);
            return (
              <Button
                key={i}
                variant={isCancel ? "outlined" : "contained"}
                startIcon={btn.icon}
                onClick={() => btn.onClick(obj)}
                disableElevation
                sx={{
                  ...(isCancel
                    ? cancelBtnSx
                    : primaryBtnSx
                  ),
                }}
              >
                {btn.name}
              </Button>
            );
          })
        ) : (
          <>
            <Button
              variant="text"
              onClick={onClose}
              disableElevation
              sx={{
                textTransform: "none", fontWeight: 600, fontSize: "0.78rem",
                borderRadius: "8px", px: 1.75, py: 0.65,
                background: cancelBg, color: cancelCl,
                "&:hover": { background: isDark ? "rgba(255,255,255,.10)" : "#E2E8F0" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => onSubmit ? onSubmit(obj) : onClose()}
              disableElevation
              sx={{
                textTransform: "none", fontWeight: 600, fontSize: "0.78rem",
                borderRadius: "8px", px: 1.75, py: 0.65,
                background: cfg.okColor, color: "#fff",
                "&:hover": { background: cfg.okColor, opacity: 0.88 },
              }}
            >
              {variant === "confirm" ? "Confirm" : "OK"}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default OptionsPopup;